// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ImprovePageLinkModal from "@steiner/App/Components/ImprovePageLink/ImprovePageLinkModal";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface MiniSignInProps {
    accountManagementLinks?: PageLinkModel[];
    orderManagementLinks?: PageLinkModel[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: MiniSignInProps) => {
    const session = getSession(state);
    const billToState = getBillToState(state, getSession(state).billToId);
    const shipToState = getShipToState(state, getSession(state).shipToId);
    return {
        displaySessionChangeCustomerLink: session?.displayChangeCustomerLink,
        viewCustomerDocuments: session?.properties?.viewCustomerDocuments === "true",
        customerDocumentLibraryLink:
            billToState?.value?.properties.customerDocumentLibraryLink ||
            shipToState?.value?.properties.customerDocumentLibraryLink,
        isPunchOutSession: getIsPunchOutSession(state),
        customerNameBillTo: billToState?.value?.customerName,
        customerNameShipTo: shipToState?.value?.customerName,
        changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    linksContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    titleContainer?: InjectableCss;
    gridContainer?: GridContainerProps;
    linkGridItem?: GridItemProps;
    link?: LinkPresentationProps;
    sessionGridItem?: GridItemProps;
    sessionLabel?: TypographyPresentationProps;
    sessionValue?: TypographyPresentationProps;
    signOutContainer?: InjectableCss;
    billToButton?: ButtonPresentationProps;
    signOutButton?: ButtonPresentationProps;
    documentLink?: LinkPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({
    accountManagementLinks,
    orderManagementLinks,
    displayChangeCustomerLink,
    onSignOutHandler,
    extendedStyles,
    displaySessionChangeCustomerLink,
    customerNameBillTo,
    customerNameShipTo,
    changeCustomerPageUrl,
    isPunchOutSession,
    customerDocumentLibraryLink,
    viewCustomerDocuments,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const history = useHistory();

    const myFilteredAccountManagementLinks = accountManagementLinks?.filter(
        pageLink => !(pageLink && pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    );

    const myFilteredOrderManagementLinks = orderManagementLinks?.filter(
        pageLink => !(pageLink && pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    );

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const onChangeBillTo = () => {
        if (changeCustomerPageUrl) {
            history.push(changeCustomerPageUrl);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="header-mini-myAccount">
            <StyledWrapper {...styles.linksContainer}>
                <Typography {...styles.title}>{translate("Account Management")}</Typography>
                <GridContainer {...styles.gridContainer}>
                    {myFilteredAccountManagementLinks?.map(
                        link =>
                            link?.url && (
                                <GridItem key={`${link.title}_${link.url}`} {...styles.linkGridItem}>
                                    <Link href={link?.url} {...styles.link}>
                                        {link?.title}
                                    </Link>
                                </GridItem>
                            ),
                    )}
                    {viewCustomerDocuments && (
                        <GridItem {...styles.linkGridItem}>
                            <Link {...styles.link} onClick={_ => setModalIsOpen(true)}>
                                {translate("My Documents")}
                            </Link>
                        </GridItem>
                    )}
                </GridContainer>
                <Typography {...styles.title}>{translate("Product & Order Management")}</Typography>
                <GridContainer {...styles.gridContainer}>
                    {myFilteredOrderManagementLinks?.map(
                        link =>
                            link?.url && (
                                <GridItem key={`${link.title}_${link.url}`} {...styles.linkGridItem}>
                                    <Link href={link?.url} {...styles.link}>
                                        {link?.title}
                                    </Link>
                                </GridItem>
                            ),
                    )}
                </GridContainer>
                {(customerNameBillTo || customerNameShipTo) && (
                    <>
                        <Typography {...styles.title}>{translate("Customer Info")}</Typography>
                        <GridContainer {...styles.gridContainer}>
                            <GridItem {...styles.sessionGridItem}>
                                <Typography {...styles.sessionLabel}>{translate("Bill-To")}:</Typography>
                                <Typography {...styles.sessionValue}>{customerNameBillTo}</Typography>
                            </GridItem>
                            <GridItem {...styles.sessionGridItem}>
                                <Typography {...styles.sessionLabel}>{translate("Ship-To")}:</Typography>
                                <Typography {...styles.sessionValue}>{customerNameShipTo}</Typography>
                            </GridItem>
                        </GridContainer>
                    </>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.signOutContainer}>
                {displaySessionChangeCustomerLink && (
                    <Button
                        {...styles.billToButton}
                        onClick={onChangeBillTo}
                        data-test-selector="header-changes-billto-shipto"
                    >
                        {translate("Change Bill-To / Ship-To")}
                    </Button>
                )}
                <Button {...styles.signOutButton} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
            <Modal
                headline={siteMessage("STR_My_Documents_Modal_Description", "Documents link")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                sizeVariant="small"
            >
                {customerDocumentLibraryLink && (
                    <Link href={customerDocumentLibraryLink} {...styles.documentLink}>
                        {customerDocumentLibraryLink}
                    </Link>
                )}
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniMyAccount);
